import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { useCart } from "@/store/cart";
import { useNavigate } from "react-router-dom";
import { Trash2, Plus, Minus, Truck, MapPin, Globe, Home } from "lucide-react";
import { useEffect, useState } from "react";
import { api, formatErr } from "@/lib/api";
import { toast } from "sonner";
import { fileUrl } from "@/components/UploadButton";
import { useConfig } from "@/context/ConfigContext";

export default function CartDrawer() {
  const { items, open, setOpen, total, updateQty, remove, clear } = useCart();
  const config = useConfig();
  const [loading, setLoading] = useState(false);
  const [method, setMethod] = useState("ship_nz"); // ship_nz | pickup_invercargill | delivery_invercargill | international
  const [shippingClasses, setShippingClasses] = useState([]);
  const [productClassMap, setProductClassMap] = useState({}); // product_id -> { name, price }
  const nav = useNavigate();

  // Fetch shipping classes once; resolve per-item class lazily from products endpoint
  useEffect(() => {
    api.get("/shipping-classes").then((r) => setShippingClasses(r.data)).catch(() => {});
  }, []);

  // For each cart item, resolve its shipping class via the product endpoint (cheap, cached by browser)
  // (Implementation lives in the next effect — left here as a placeholder for clarity.)

  // Pull the active product catalog once when the drawer opens and build a per-product
  // shipping-class lookup keyed by product id.
  useEffect(() => {
    if (!open) return;
    api.get("/products").then((r) => {
      const m = {};
      const classById = Object.fromEntries(shippingClasses.map((c) => [c.id, c]));
      const defaultClass = shippingClasses.find((c) => c.is_default) || shippingClasses[0];
      r.data.forEach((p) => {
        const cls = classById[p.shipping_class_id] || defaultClass || { name: "Standard", price: 35 };
        m[p.id] = {
          name: p.free_shipping ? "Free Shipping" : cls.name,
          price: p.free_shipping ? 0 : cls.price,
          free_shipping: !!p.free_shipping,
          is_special: !!p.is_special,
          category: p.category,
        };
      });
      setProductClassMap(m);
    }).catch(() => {});
  }, [open, shippingClasses]);

  const subtotal = total();
  const lineShipping = items.map((i) => {
    const cls = productClassMap[i.product_id];
    const unit = cls?.price ?? 0;
    return {
      item: i,
      className: cls?.name || "—",
      unit,
      line: unit * i.quantity,
    };
  });
  const shippingCost = method === "ship_nz"
    ? lineShipping.reduce((s, r) => s + r.line, 0)
    : method === "delivery_invercargill"
      ? config.local_delivery_fee_nz
      : 0;

  // Stealth local pickup discount — refund the $25 "Smart Specials" bump for pickup buyers.
  // Must match SPECIAL_INCREMENT in the backend.
  const SPECIAL_INCREMENT = 25;
  // Match the backend clamp: per-unit discount can never exceed the unit price.
  const perUnitPickupDiscount = (unitPrice) => Math.min(SPECIAL_INCREMENT, Number(unitPrice) || 0);
  const potentialPickupDiscount = items.reduce(
    (s, i) => s + (productClassMap[i.product_id]?.is_special ? perUnitPickupDiscount(i.price) * i.quantity : 0),
    0,
  );
  const specialItemCount = items.reduce(
    (s, i) => s + (productClassMap[i.product_id]?.is_special ? i.quantity : 0),
    0,
  );
  const localPickupDiscount = method === "pickup_invercargill"
    ? items.reduce((s, i) => {
        const cls = productClassMap[i.product_id];
        if (!cls?.is_special) return s;
        return s + perUnitPickupDiscount(i.price) * i.quantity;
      }, 0)
    : 0;
  const grandTotal = Math.max(0, subtotal + shippingCost - localPickupDiscount);

  const checkout = async () => {
    if (items.length === 0) return;
    if (method === "international") return;
    setLoading(true);
    try {
      const payload = {
        items: items.map((i) => ({ product_id: i.product_id, quantity: i.quantity })),
        origin_url: window.location.origin,
        shipping_method: method,
      };
      const r = await api.post("/checkout/session", payload);
      window.location.href = r.data.url;
    } catch (e) {
      toast.error(formatErr(e));
      setLoading(false);
    }
  };

  const options = [
    {
      value: "pickup_invercargill",
      title: `Local Pickup (${config.pickup_location})`,
      sub: `Free · Email ${config.contact_email} to arrange a pickup time at the workshop`,
      hint: "No shipping address required — David will coordinate the pickup time directly with you after payment.",
      icon: MapPin,
    },
    {
      value: "delivery_invercargill",
      title: "Local delivery (Invercargill only)",
      sub: `Flat $${config.local_delivery_fee_nz.toFixed(2)} · we'll arrange a delivery time`,
      hint: "Available for Invercargill addresses only.",
      icon: Home,
    },
    {
      value: "ship_nz",
      title: "Ship within New Zealand",
      sub: "Per-item courier rates",
      icon: Truck,
    },
    {
      value: "international",
      title: "Overseas / international",
      sub: `Email ${config.contact_email} first for a shipping quote`,
      icon: Globe,
    },
  ];

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent
        data-testid="cart-drawer"
        side="right"
        className="bg-[#E0F2F1] border-l border-[#B2DFDB] text-[#212121] w-full sm:max-w-md flex flex-col"
      >
        <SheetHeader>
          <SheetTitle className="font-display text-3xl text-[#212121] font-light tracking-tight">Your Cart</SheetTitle>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto mt-6 -mx-6 px-6">
          {items.length === 0 ? (
            <p className="text-[#757575] mt-12 text-center" data-testid="cart-empty">Your cart is empty.</p>
          ) : (
            <>
              <ul className="space-y-6">
                {items.map((i) => (
                  <li key={i.product_id} data-testid={`cart-item-${i.product_id}`} className="flex gap-4 pb-6 border-b border-[#B2DFDB]">
                    {i.image && (
                      <img src={fileUrl(i.image)} alt={i.name} className="w-20 h-20 object-cover rounded-sm border border-[#B2DFDB]" />
                    )}
                    <div className="flex-1">
                      <p className="font-display text-lg leading-tight">{i.name}</p>
                      <p className="mt-1">
                        {i.on_sale && i.original_price > i.price && (
                          <span className="text-gray-400 text-sm line-through mr-2">${Number(i.original_price).toFixed(2)}</span>
                        )}
                        <span className="text-[#212121] font-bold">${Number(i.price).toFixed(2)}</span>
                      </p>
                      <div className="flex items-center gap-3 mt-3">
                        <button onClick={() => updateQty(i.product_id, i.quantity - 1)} className="border border-[#B2DFDB] p-1 hover:border-[#B87333]" data-testid={`qty-minus-${i.product_id}`}><Minus size={12} /></button>
                        <span className="text-sm">{i.quantity}</span>
                        <button onClick={() => updateQty(i.product_id, i.quantity + 1)} className="border border-[#B2DFDB] p-1 hover:border-[#B87333]" data-testid={`qty-plus-${i.product_id}`}><Plus size={12} /></button>
                        <button onClick={() => remove(i.product_id)} className="ml-auto text-[#757575] hover:text-[#D32F2F]" data-testid={`remove-${i.product_id}`}><Trash2 size={16} /></button>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>

              <div className="mt-8 pt-6 border-t border-[#B2DFDB]">
                <p className="smallcaps mb-4">Delivery</p>
                <div className="space-y-2">
                  {options.map((o) => {
                    const Icon = o.icon;
                    const selected = method === o.value;
                    return (
                      <button
                        key={o.value}
                        onClick={() => setMethod(o.value)}
                        data-testid={`shipping-${o.value}`}
                        className={`w-full text-left p-3 border transition-colors flex gap-3 ${selected ? "border-[#B87333] bg-[#FFFFFF]" : "border-[#B2DFDB] hover:border-[#B87333]/60"}`}
                      >
                        <Icon size={18} className={`mt-0.5 flex-shrink-0 ${selected ? "text-[#B87333] font-bold" : "text-[#757575]"}`} />
                        <div className="flex-1">
                          <p className={`text-sm uppercase tracking-wider ${selected ? "text-[#B87333] font-bold" : "text-[#212121]"}`}>{o.title}</p>
                          <p className="text-xs text-[#757575] mt-0.5">{o.sub}</p>
                          {o.hint && (
                            <p className="text-[10px] text-[#9E9E9E] mt-1 italic">{o.hint}</p>
                          )}
                          {o.value === "pickup_invercargill" && potentialPickupDiscount > 0 && (
                            <p
                              className="text-[11px] font-black text-[#B87333] mt-1.5 tracking-wide"
                              data-testid="pickup-nudge"
                            >
                              ★ Save ${potentialPickupDiscount.toFixed(2)} on pickup
                              {specialItemCount > 1 && ` (${specialItemCount} special items)`}
                            </p>
                          )}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Per-item shipping breakdown */}
              {method === "ship_nz" && lineShipping.length > 0 && (
                <div className="mt-6 pt-6 border-t border-[#B2DFDB]" data-testid="shipping-breakdown">
                  <p className="smallcaps mb-3">Shipping breakdown</p>
                  <ul className="space-y-2 text-sm">
                    {lineShipping.map((row) => (
                      <li key={row.item.product_id} className="flex justify-between gap-3" data-testid={`ship-line-${row.item.product_id}`}>
                        <span className="text-[#757575] truncate">
                          {row.item.quantity}× {row.item.name}
                          <span className="text-[#9E9E9E]"> · {row.className}</span>
                        </span>
                        <span className={`flex-shrink-0 font-semibold ${productClassMap[row.item.product_id]?.free_shipping ? "text-[#00BFA5] font-bold" : "text-[#212121]"}`}>
                          {productClassMap[row.item.product_id]?.free_shipping ? "FREE" : `$${row.line.toFixed(2)}`}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </>
          )}
        </div>

        {items.length > 0 && (
          <div className="pt-6 border-t border-[#B2DFDB] space-y-3">
            {method !== "international" && (
              <>
                <div className="flex justify-between text-sm">
                  <span className="text-[#757575]">Subtotal</span>
                  <span className="text-[#212121]" data-testid="cart-subtotal">${subtotal.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-[#757575]">Shipping</span>
                  <span className={`font-semibold ${shippingCost === 0 && method === "ship_nz" ? "text-[#00BFA5] font-bold" : "text-[#212121]"}`} data-testid="cart-shipping">
                    {shippingCost === 0 ? (method === "ship_nz" ? "FREE SHIPPING" : "Free") : `$${shippingCost.toFixed(2)}`}
                  </span>
                </div>
                {localPickupDiscount > 0 && (
                  <div className="flex justify-between text-sm" data-testid="cart-local-pickup-discount">
                    <span className="text-[#B87333] font-bold">Local Pickup Discount</span>
                    <span className="text-[#B87333] font-black">−${localPickupDiscount.toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between pt-3 border-t border-[#B2DFDB]">
                  <span className="smallcaps">Total</span>
                  <span className="font-display text-2xl" data-testid="cart-total">${grandTotal.toFixed(2)}</span>
                </div>
                {method === "pickup_invercargill" && (
                  <div
                    className="border border-[#B87333]/60 bg-[#B2DFDB] p-3 text-xs leading-relaxed"
                    data-testid="pickup-notice"
                  >
                    <p className="text-[#B87333] font-bold uppercase tracking-widest text-[10px] mb-1.5 font-medium">⚑ Action after payment</p>
                    <p className="text-[#212121]">
                      Please email{" "}
                      <a href={`mailto:${config.contact_email}?subject=Pickup arrangement for my order`} className="text-[#B87333] font-bold underline">
                        {config.contact_email}
                      </a>{" "}
                      to arrange a pickup time at the workshop. No shipping address is needed.
                    </p>
                  </div>
                )}
                <button onClick={checkout} disabled={loading} className="btn-primary w-full justify-center" data-testid="checkout-btn">
                  {loading ? "Preparing…" : "Checkout"}
                </button>
                <p className="text-center text-xs text-[#A3A3A3] mt-1" data-testid="checkout-help-hint">
                  Need help?{" "}
                  <a
                    href={`mailto:${config.contact_email}?subject=Help%20with%20my%20order`}
                    className="text-[#B87333] font-bold underline hover:text-[#F5F5DC]"
                    data-testid="checkout-help-link"
                  >
                    Contact David
                  </a>
                </p>
              </>
            )}
            {method === "international" && (
              <div className="border border-[#B87333]/40 bg-[#FFFFFF] p-4" data-testid="international-notice">
                <p className="smallcaps text-[#B87333] font-bold mb-2">Email us first</p>
                <p className="text-sm text-[#212121] leading-relaxed mb-3">
                  Overseas shipping is quoted case by case — every package is packed carefully and tracked.
                  Send us the pieces you&apos;re after and your country, and we&apos;ll come back with a quote within 48 hours.
                </p>
                <a href={`mailto:${config.contact_email}?subject=International%20order%20enquiry`} className="btn-primary w-full justify-center" data-testid="international-email-btn">
                  Email {config.contact_email}
                </a>
              </div>
            )}
            <button onClick={clear} className="btn-ghost w-full text-xs uppercase tracking-widest" data-testid="clear-cart-btn">Clear cart</button>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
